import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/images/logo.png';
import {
  LayoutDashboard,
  Building2,
  Calendar,
  Newspaper,
  Download,
  Users,
  HeartHandshake,
  Coins,
  MessageSquare,
  Settings,
  LogOut,
  ExternalLink,
  X,
  GraduationCap,
} from 'lucide-react';

const navItems = [
  { to: '/admin/dashboard', icon: LayoutDashboard, labelEn: 'Dashboard', labelNe: 'ड्यासबोर्ड' },
  { to: '/admin/sectors', icon: Building2, labelEn: 'Sectors', labelNe: 'क्षेत्रहरू' },
  { to: '/admin/events', icon: Calendar, labelEn: 'Events', labelNe: 'कार्यक्रमहरू' },
  { to: '/admin/news', icon: Newspaper, labelEn: 'News', labelNe: 'समाचार' },
  { to: '/admin/downloads', icon: Download, labelEn: 'Downloads', labelNe: 'डाउनलोड' },
  { to: '/admin/experts', icon: GraduationCap, labelEn: 'Experts', labelNe: 'विशेषज्ञहरू' },
  { to: '/admin/volunteers', icon: HeartHandshake, labelEn: 'Volunteers', labelNe: 'स्वयंसेवकहरू' },
  { to: '/admin/contributions', icon: Coins, labelEn: 'Contributions', labelNe: 'योगदानहरू' },
  { to: '/admin/feedback', icon: MessageSquare, labelEn: 'Feedback', labelNe: 'प्रतिक्रिया' },
  { to: '/admin/users', icon: Users, labelEn: 'Users', labelNe: 'प्रयोगकर्ताहरू' },
  { to: '/admin/settings', icon: Settings, labelEn: 'Settings', labelNe: 'सेटिङ' },
];

const AdminSidebar = ({ open, onClose, language }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleReturnToSite = () => {
    navigate('/');
  };

  const sidebarContent = (
    <div className="flex h-full flex-col bg-white">
      {/* Logo */}
      <div className="flex items-center justify-between border-b border-char-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Bholiko Itahari" className="h-9 w-9 rounded-xl object-cover" />
          <div>
            <p className="text-sm font-extrabold text-char-900 leading-tight">
              Bholiko Itahari
            </p>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              Admin Panel
            </p>
          </div>
        </div>
        <button onClick={onClose} className="rounded-lg p-1.5 text-char-400 hover:bg-char-50 lg:hidden">
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {navItems.map(({ to, icon: Icon, labelEn, labelNe }) => (
            <li key={to}>
              <NavLink
                to={to}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-100 text-primary shadow-sm'
                      : 'text-char-600 hover:bg-char-50 hover:text-char-900'
                  }`
                }
              >
                <Icon size={18} />
                <span>{language === 'ne' ? labelNe : labelEn}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-char-100 px-3 py-4 space-y-2">
        <button
          onClick={handleReturnToSite}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-char-600 transition-all duration-200 hover:bg-char-50 hover:text-char-900"
        >
          <ExternalLink size={18} />
          <span>{language === 'ne' ? 'साइटमा फर्कनुहोस्' : 'Return to Site'}</span>
        </button>
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-char-600 transition-all duration-200 hover:bg-error-50 hover:text-error"
        >
          <LogOut size={18} />
          <span>{language === 'ne' ? 'लगआउट' : 'Logout'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-64 border-r border-char-100 shadow-sm lg:block">
        {sidebarContent}
      </aside>

      {/* Mobile drawer overlay */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-char-900/40 backdrop-blur-sm" onClick={onClose} />
          <aside className="relative h-screen w-72 shadow-2xl animate-in slide-in-from-left">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;
